
import { defineComponent } from "vue";
import axios from "@/axios-instance";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import moment from "moment";

export default defineComponent({
  name: "sync-info-banner",
  props: {
    // id phiếu nhập học (applicant.id) cần kiểm tra/đồng bộ
    applicantId: { type: [String, Number], default: null },
    // false trên phiếu confirmed/cancel -> chỉ xem thông tin lệch, ẩn nút đồng bộ
    canSync: { type: Boolean, default: true },
    // hiện dòng xanh "Thông tin khớp" khi không lệch (drawer: có; trang chi tiết: thường tắt cho gọn)
    showMatchNote: { type: Boolean, default: true },
  },
  emits: ["synced"],
  computed: {
    //cột lệch của 1 bên TRỐNG HẾT -> nguồn đó vô nghĩa (BE bỏ qua trường trống) -> ẩn nút lấy bên đó
    applicantAllEmpty(): boolean {
      const empty = (v: any) => !v || v === "—";
      return this.syncDiffs.length > 0 && this.syncDiffs.every((d: any) => empty(d.applicant));
    },
    studentAllEmpty(): boolean {
      const empty = (v: any) => v === null || !v || v === "—";
      return this.syncDiffs.length > 0 && this.syncDiffs.every((d: any) => empty(d.student));
    },
  },
  data() {
    return {
      syncChecked: false,
      syncing: false,
      syncDiffs: [] as any[],
    };
  },
  watch: {
    applicantId: {
      immediate: true,
      handler(id: any) {
        if (id) { this.check(id); }
        else { this.syncDiffs = []; this.syncChecked = false; }
      },
    },
  },
  methods: {
    moment(date: any) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    // Tải + tính danh sách field lệch giữa 3 bảng. So lỏng: bỏ hoa/thường + khoảng trắng.
    async check(id: any) {
      this.syncChecked = false;
      this.syncDiffs = [];
      if (!id) { return; }
      try {
        const { data } = await axios.get(`applicant/syncinfo/${id}`);
        const norm: any = {
          fullName: (v: any) => String(v || "").replace(/\s+/g, " ").trim().toUpperCase(),
          idCard: (v: any) => String(v || "").trim(),
          dateOfBirth: (v: any) => String(v || "").slice(0, 10),
          phone: (v: any) => String(v || "").replace(/\s+/g, ""),
          email: (v: any) => String(v || "").trim().toLowerCase(),
          //BE đã quy 3 bảng về cùng 'male'|'female'|null (students/winners lưu BOOLEAN, applicants lưu STRING)
          gender: (v: any) => String(v || "").trim().toLowerCase(),
        };
        const labels: any = { fullName: "Họ tên", idCard: "CCCD", dateOfBirth: "Ngày sinh", phone: "SĐT", email: "Email", gender: "Giới tính" };
        // Địa chỉ thường trú (TT) + liên lạc (LL): báo lệch khi 2 bên CÙNG có mà khác, hoặc phiếu nhập học TRỐNG mà
        // bảng khác có (đáng đồng bộ bù); bên kia trống thì bỏ qua (hồ sơ cũ chưa migrate xã — báo hết thì spam giả).
        const addressLabels: any = {
          prAddress: "Địa chỉ TT", prWard: "Phường/Xã (TT)", prProvince: "Tỉnh/TP (TT)",
          poAddress: "Địa chỉ LL", poWard: "Phường/Xã (LL)", poProvince: "Tỉnh/TP (LL)",
        };
        const normText = (v: any) => String(v || "").replace(/\s+/g, " ").trim().toLowerCase();
        const diffs: any[] = [];
        Object.keys(labels).forEach((k) => {
          const a = data.applicant ? data.applicant[k] : null;
          const s = data.student ? data.student[k] : null;
          const w = data.adwinner ? data.adwinner[k] : null;
          const na = norm[k](a);
          // chỉ so với bảng có bản ghi; bảng thiếu (hồ sơ cũ) thì bỏ qua
          const mismatch = (data.student && norm[k](s) !== na) || (data.adwinner && norm[k](w) !== na);
          if (mismatch) {
            //gender về từ BE là 'male'/'female' -> hiển thị tiếng Việt cho người đọc bảng lệch
            const disp = (v: any) => (k === "dateOfBirth" ? this.moment(v)
              : k === "gender" ? ({ male: "Nam", female: "Nữ" } as any)[String(v || "").toLowerCase()]
                : v) || "—";
            diffs.push({
              key: k, label: labels[k],
              applicant: disp(a),
              student: data.student ? disp(s) : null,
              winner: data.adwinner ? disp(w) : null,
            });
          }
        });
        Object.keys(addressLabels).forEach((k) => {
          const a = data.applicant ? data.applicant[k] : null;
          const s = data.student ? data.student[k] : null;
          const w = data.adwinner ? data.adwinner[k] : null;
          const na = normText(a);
          const mismatch = (na && normText(s) && normText(s) !== na) || (na && normText(w) && normText(w) !== na)
            || (!na && (normText(s) || normText(w))); // nhập học trống mà bên kia có -> cũng đáng xem
          if (mismatch) {
            diffs.push({
              key: k, label: addressLabels[k],
              applicant: a || "—",
              student: data.student ? (s || "—") : null,
              winner: data.adwinner ? (w || "—") : null,
            });
          }
        });
        // Phần cá nhân: nơi sinh / ngày-nơi cấp CCCD / dân tộc / tôn giáo / quốc tịch.
        // Cùng guard "chỉ báo khi 2 bên đều có mà khác, hoặc nhập học trống mà bên kia có". Winner KHÔNG lưu
        // ngày cấp/dân tộc/tôn giáo/quốc tịch -> các key đó luôn null nên guard tự bỏ qua winner (khỏi báo giả).
        const personalLabels: any = {
          placeOfBirth: "Nơi sinh", issuedDate: "Ngày cấp CCCD", issuedBy: "Nơi cấp CCCD",
          ethnic: "Dân tộc", religion: "Tôn giáo", nationality: "Quốc tịch",
        };
        Object.keys(personalLabels).forEach((k) => {
          const isDate = k === "issuedDate";
          const nm = (v: any) => isDate ? String(v || "").slice(0, 10) : normText(v);
          const a = data.applicant ? data.applicant[k] : null;
          const s = data.student ? data.student[k] : null;
          const w = data.adwinner ? data.adwinner[k] : null;
          const na = nm(a);
          // Lệch cả khi applicant CÓ mà student TRỐNG (cần đồng bộ applicant->student) — giống phần định danh.
          // Winner CHỈ so khi winner CÓ giá trị (winner không lưu ngày cấp/dân tộc/tôn giáo/quốc tịch -> tránh báo giả).
          const mismatch = (data.student && na && nm(s) !== na) || (na && nm(w) && nm(w) !== na)
            || (!na && (nm(s) || nm(w)));
          if (mismatch) {
            const disp = (v: any) => (isDate ? this.moment(v) : v) || "—";
            diffs.push({
              key: k, label: personalLabels[k],
              applicant: disp(a),
              student: data.student ? disp(s) : null,
              winner: data.adwinner ? disp(w) : null,
            });
          }
        });
        this.syncDiffs = diffs;
        this.syncChecked = true;
      } catch (e) {
        // lỗi mạng/BE cũ chưa có endpoint: không chặn trang, chỉ ẩn phần so lệch
        this.syncChecked = false;
      }
    },
    // Đồng bộ theo NGUỒN: 'applicant' (phiếu nhập học) | 'student' (hồ sơ ứng viên)
    syncIdentity(source: string) {
      const src = source === "student" ? "student" : "applicant";
      //CHỈ đồng bộ đúng các trường ĐANG LỆCH trong banner (gửi kèm fields cho BE) — popup liệt kê chính
      //các trường đó bằng ngôn ngữ thường, khớp 1:1 với bảng user vừa nhìn
      const srcLabel = src === "applicant" ? "phiếu NHẬP HỌC" : "hồ sơ ỨNG VIÊN";
      const dstLabel = src === "applicant" ? "hồ sơ Ứng viên và Trúng tuyển" : "phiếu Nhập học và Trúng tuyển";
      //Câu hỏi chiều ghi + bảng "đang có → sẽ thành" TỪNG TRƯỜNG (dữ liệu sẵn trong syncDiffs) —
      //user xác nhận đúng giá trị sẽ ghi, không phải đoán. Cột "đang có" lấy theo bảng đích chính
      //(nguồn Ứng viên -> đích chính là Nhập học và ngược lại; Trúng tuyển ăn theo).
      //Từng trường: thông tin SAI bị GẠCH NGANG → thông tin ĐÚNG tô đậm (cùng ngôn ngữ với modal
      //đối soát điểm Bộ: giá trị bị thay thì gạch) — tự đọc được, không cần header cột
      const esc = (v: any) => String(v ?? "—").replace(/</g, "&lt;");
      const dstKey = src === "student" ? "applicant" : "student";
      const rows = this.syncDiffs.map((d: any) =>
        `<div class="mb-1"><span class="text-gray-700">${esc(d.label)}:</span> ` +
        `<span class="text-decoration-line-through text-muted">${esc(d[dstKey])}</span>` +
        `<i class="bi bi-arrow-right text-gray-500 mx-2"></i>` +
        `<b class="text-primary">${esc(d[src])}</b></div>`).join("");
      let html =
        `<div class="text-start fs-6 mb-3">Lấy dữ liệu bên <b>${srcLabel}</b> ghi đè sang <b>${dstLabel}</b>?</div>` +
        `<div class="text-start fs-7" style="max-height:220px;overflow-y:auto">${rows}</div>`;
      // Gợi ý hướng đồng bộ: đếm trường mà NGUỒN chọn đang TRỐNG nhưng bên đối chiếu CÓ.
      // BE _skipEmpty bỏ qua trường trống của nguồn -> chọn nhầm nguồn rỗng = no-op, lệch vẫn còn.
      const isEmpty = (v: any) => !v || v === "—";
      const otherKey = src === "student" ? "applicant" : "student";
      const skipped = this.syncDiffs.filter((d: any) => isEmpty(d[src]) && !isEmpty(d[otherKey]));
      if (skipped.length) {
        const otherLabel = src === "student" ? "phiếu nhập học" : "hồ sơ ứng viên";
        const oppositeBtn = src === "student" ? "Theo phiếu nhập học" : "Theo hồ sơ ứng viên";
        html =
          `<div style="text-align:left;background:#fff8e1;border:1px dashed #f0ad4e;border-radius:6px;padding:8px 10px;margin-bottom:10px;font-size:13px;color:#8a6d3b">` +
          `⚠️ Nguồn bạn chọn đang <b>thiếu ${skipped.length} trường</b> (${skipped.map((d: any) => d.label).join(", ")}) mà ${otherLabel} CÓ → sẽ <b>bị bỏ qua</b>, lệch có thể vẫn còn.<br/>` +
          `Cân nhắc chọn <b>“${oppositeBtn}”</b> nếu muốn lấp dữ liệu trống.` +
          `</div>` + html;
      }
      Swal.fire({
        title: "Đồng bộ thông tin",
        html,
        icon: "warning",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Đồng bộ",
        cancelButtonText: "Trở lại",
        customClass: {
          confirmButton: "btn btn-primary order-2",
          cancelButton: "btn btn-light order-1",
        },
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        this.syncing = true;
        try {
          //fields = các trường ĐANG LỆCH — BE chỉ ghi đè đúng các trường này (không quét cả bộ)
          const { data } = await axios.put(`applicant/syncinfo/${this.applicantId}`, { source: src, fields: this.syncDiffs.map((d: any) => d.key) });
          await this.check(this.applicantId); // so lại theo bản vừa đồng bộ
          Swal.fire({ text: data?.message || "Đã đồng bộ.", icon: "success", timer: 2000, showConfirmButton: false });
          this.$emit("synced"); // để trang cha nạp lại dữ liệu đang hiển thị
        } catch (e: any) {
          Swal.fire({
            text: e?.response?.data?.errorMessage || e?.response?.data?.message || "Đồng bộ thất bại, vui lòng thử lại.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Đóng",
            customClass: { confirmButton: "btn btn-primary" },
          });
        }
        this.syncing = false;
      });
    },
  },
});
